import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosApi from "../server/service";
import toast from "react-hot-toast";
import {

  ArrowLeft,
  Plus,
  Search,

  Loader2,

  Factory,
} from "lucide-react";

import {
    Input, Button, Badge,
    ShadcnTable, ShadcnTableHeader, ShadcnTableBody, ShadcnTableRow, ShadcnTableHead, ShadcnTableCell,
    ActionMenu
} from "../component/ui/TableComponent";



interface IApiVendor {
    _id: string; vendorId: string; vendorName: string; vendorType: string;
    contactNumber: string; address?: string; status: 'Active' | 'Inactive';
    emailAddress?: string; gstNumber?: string; additionalNotes?: string;
}
interface IVendorData extends Omit<IApiVendor, '_id' | 'vendorName'> {
    id: string; name: string;
}



// --- MAIN PAGE COMPONENT ---
const VendorMaster: React.FC = () => {
    const navigate = useNavigate();
    const [vendors, setVendors] = useState<IVendorData[]>([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [searchInput, setSearchInput] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [itemToDelete, setItemToDelete] = useState<IVendorData | null>(null);

    const totalPages = Math.ceil(totalCount / pageSize);

    const fetchVendors = useCallback(async (pageToFetch: number) => {
        setLoading(true); setFetchError(null);
        const params = new URLSearchParams({ page: String(pageToFetch), limit: String(pageSize) });
        if (typeFilter !== "all") params.append("vendorType", typeFilter);
        if (statusFilter !== "all") params.append("status", statusFilter);
        if (searchInput) params.append("search", searchInput);
        try {
            const { data } = await axiosApi.get(`/api/vendors/getVendors?${params.toString()}`);
            const { vendors: apiVendors, totalCount: newTotalCount } = data.data;

            
            setVendors(apiVendors.map((v: IApiVendor) => ({
                id: v._id, name: v.vendorName, vendorId: v.vendorId, vendorType: v.vendorType,
                contactNumber: v.contactNumber, address: v.address, status: v.status,
                emailAddress: v.emailAddress, gstNumber: v.gstNumber, additionalNotes: v.additionalNotes ,vendorName: v.vendorName,
            })));
            setTotalCount(newTotalCount);
        } catch (err: any) {
            setFetchError(err?.response?.data?.message || "Failed to load vendors");
        } finally { setLoading(false); }
    }, [pageSize, typeFilter, statusFilter, searchInput]);

    useEffect(() => {
        const handler = setTimeout(() => {
            if (currentPage !== 1) setCurrentPage(1);
            else fetchVendors(1);
        }, 300);
        return () => clearTimeout(handler);
    }, [searchInput, typeFilter, statusFilter]);
    
    useEffect(() => { fetchVendors(currentPage); }, [currentPage, fetchVendors]);

    const handleEditClick = (vendor: IVendorData) => {
        navigate('/createVendor', { state: { vendor } });
    };
    
    const confirmDelete = useCallback(async () => {
        if (!itemToDelete) return;
        const toastId = toast.loading("Deleting vendor...");
        try {
            await axiosApi.delete(`/api/vendors/delete/${itemToDelete.id}`);
            toast.success("Vendor deleted!", { id: toastId });
            if (vendors.length === 1 && currentPage > 1) setCurrentPage(p => p - 1);
            else fetchVendors(currentPage);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to delete.", { id: toastId });
        } finally { setItemToDelete(null); }
    }, [itemToDelete, currentPage, vendors.length, fetchVendors]);

    return (
        <div className="bg-slate-50 min-h-screen  font-sans">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-6">
               
            <div className="flex items-center gap-10">
                      <Link
                        to="/master"
                        className="flex items-center gap-2 border bg-white border-slate-200 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="text-black font-medium">
                          Back <span className="hidden sm:inline">to Masters</span>
                        </span>
                      </Link>
                      <div>
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800">
                          Vendor Master
                        </h1>
                        <p className="text-slate-600 text-sm sm:text-base mt-1">
                          Manage vendor information and contact details
                        </p>
                      </div>
                    </div>
              
                <Button onClick={() => navigate('/createVendor')} className="bg-green-500 hover:bg-green-600 text-white">
                    <Plus className="w-5 h-5 mr-2" /> New Vendor
                </Button>
            </div>

            <div className="mt-6 bg-white shadow-md rounded-lg p-4">
                <div className="flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1 w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <Input type="text" placeholder="Search by name, ID, contact..." value={searchInput} onChange={e => setSearchInput(e.target.value)} className="pl-10" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400">
                            <option value="all">All Types</option>
                            <option value="Fabric Supplier">Fabric Supplier</option>
                            <option value="Embroidery">Embroidery</option>
                            <option value="DesignWork">Design Work</option>
                            <option value="Handwork">Handwork</option>
                            <option value="Stitching">Stitching</option>
                            <option value="Accessories">Accessories</option>
                            <option value="General">General</option>
                        </select>
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400">
                            <option value="all">All Status</option><option value="Active">Active</option><option value="Inactive">Inactive</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="mt-6 bg-white shadow-lg rounded-lg">
                {loading ? <div className="flex items-center justify-center gap-2 p-12 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /><span>Loading Vendors...</span></div>
                : fetchError ? <div className="p-12 text-center text-red-600 font-medium">{fetchError}</div>
                : vendors.length === 0 ? <div className="p-12 text-center text-slate-500">No vendors found.</div>
                : <>
                    <div className="hidden lg:block">
                        <ShadcnTable>
                            <ShadcnTableHeader><ShadcnTableRow>
                                <ShadcnTableHead>Vendor Name</ShadcnTableHead><ShadcnTableHead>Vendor ID</ShadcnTableHead>
                                <ShadcnTableHead>Type</ShadcnTableHead><ShadcnTableHead>Contact</ShadcnTableHead>
                                <ShadcnTableHead>Status</ShadcnTableHead><ShadcnTableHead className="text-center">Actions</ShadcnTableHead>
                            </ShadcnTableRow></ShadcnTableHeader>
                            <ShadcnTableBody>
                                {vendors.map(v => (
                                    <ShadcnTableRow key={v.id}>
                                        <ShadcnTableCell className="font-medium text-slate-900">{v.name}</ShadcnTableCell>
                                        <ShadcnTableCell>{v.vendorId}</ShadcnTableCell><ShadcnTableCell>{v.vendorType}</ShadcnTableCell>
                                        <ShadcnTableCell>{v.contactNumber}</ShadcnTableCell>
                                        <ShadcnTableCell><Badge variant={v.status === 'Active' ? 'success' : 'danger'}>{v.status}</Badge></ShadcnTableCell>
                                        <ShadcnTableCell className="flex justify-center items-center">
                                            <ActionMenu  onEdit={() => handleEditClick(v)} onDelete={() => setItemToDelete(v)} />
                                        </ShadcnTableCell>
                                    </ShadcnTableRow>
                                ))}
                            </ShadcnTableBody>
                        </ShadcnTable>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 lg:hidden">
                        {vendors.map(v => (
                            <div key={v.id} className="bg-white rounded-lg shadow-md border  border-gray-300 p-4 flex flex-col justify-between">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="flex-shrink-0 bg-slate-100 p-3 rounded-full"><Factory className="text-gray-500 w-6 h-6" /></div>
                                        <div>
                                            <h3 className="font-bold text-lg text-slate-900">{v.name}</h3>
                                            <p className="text-sm text-slate-600">{v.vendorId}</p>
                                        </div>
                                    </div>
                                    <ActionMenu  onEdit={() => handleEditClick(v)} onDelete={() => setItemToDelete(v)} />
                                </div>
                                <div className="mt-4 space-y-2 text-sm">
                                    <div className="flex justify-between items-center"><span className="text-slate-500">Status:</span><Badge variant={v.status === 'Active' ? 'success' : 'danger'}>{v.status}</Badge></div>
                                    <div className="flex justify-between"><span className="text-slate-500">Type:</span><span className="font-medium text-slate-800">{v.vendorType}</span></div>
                                    <div className="flex justify-between"><span className="text-slate-500">Contact:</span><span className="font-medium text-slate-800">{v.contactNumber}</span></div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between flex-wrap gap-4 p-4 border-t">
                            <span className="text-sm text-slate-600">Showing <span className="font-medium">{vendors.length}</span> of <span className="font-medium">{totalCount}</span> results</span>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1 || loading}>Previous</Button>
                                <span className="text-sm font-medium px-2">Page {currentPage} of {totalPages}</span>
                                <Button variant="outline" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages || loading}>Next</Button>
                            </div>
                        </div>
                    )}
                </>}
            </div>
            {itemToDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md m-4 p-6">
                        <h2 className="text-lg font-semibold text-slate-900">Confirm Deletion</h2>
                        <p className="mt-2 text-sm text-slate-600">Are you sure you want to delete vendor: <span className="font-bold">{itemToDelete.name}</span>?</p>
                        <div className="mt-6 flex justify-end gap-3">
                            <Button variant="outline" onClick={() => setItemToDelete(null)}>Cancel</Button>
                            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorMaster;














